import { Navigate, useSearchParams } from 'react-router-dom';
import { COMPOSE_INTENT_NEW, COMPOSE_PARAM, COMPOSE_TO_PARAM, isPlausibleAddress } from '@/app/composeIntent';

/**
 * Where the browser lands when OLS Mail is the `mailto:` handler.
 *
 * The manifest registers `/mailto?target=%s`, and the browser substitutes the
 * whole clicked URL — `mailto:alex@example.test?subject=Hi&body=…`. That string
 * comes from whatever page the person was reading, so it is treated as input,
 * not as instructions:
 *
 * - **Only the address is taken.** A `subject` or `body` from someone else's
 *   page is words put in the sender's mouth, in a window that looks like they
 *   wrote it. Nothing about being the default mail client makes that safe, and
 *   nothing in this app needs it.
 * - **Only the first address**, and only if it is plausible
 *   (`isPlausibleAddress`, which refuses controls and anything that is not one
 *   `local@domain` pair). A header smuggled through a `mailto:` is the obvious
 *   attack, and the composer's To field is a header.
 * - **Anything unusable opens an empty composer** rather than an error: the
 *   person asked to write a message, and a blank one is still that.
 *
 * `replace`, so the handler URL is not a place the back button walks through.
 */
export function MailtoRedirect() {
  const [params] = useSearchParams();
  const target = params.get('target') ?? '';
  const search = new URLSearchParams({ [COMPOSE_PARAM]: COMPOSE_INTENT_NEW });

  // Not `new URL()`: a malformed value would throw, and the answer to a
  // malformed mailto is an empty composer, not a crashed route.
  const withoutScheme = target.replace(/^mailto:/i, '');
  const firstAddress = decodeURIComponent(withoutScheme.split('?')[0]?.split(',')[0] ?? '').trim();
  if (isPlausibleAddress(firstAddress)) search.set(COMPOSE_TO_PARAM, firstAddress);

  return <Navigate to={{ pathname: '/mail', search: `?${search.toString()}` }} replace />;
}
